import { useQuery } from '@tanstack/react-query';
import { valueSetsService } from '../services/value-sets.service';

export interface SelectOption {
  value: string;
  label: string;
}

/**
 * Dropdown options sourced from an admin-editable value set (by key), falling
 * back to a built-in list until the set is seeded.
 *
 * This is the safe path for turning a hardcoded dropdown into a value-set-driven
 * one: when the value set exists the control is admin-editable via the Value Sets
 * screen; when it does not (not seeded yet), the `fallback` keeps the dropdown
 * working instead of silently emptying. Once the backend seeds the key, the
 * dropdown switches to the value set automatically with no code change.
 */
export function useValueSetOptions(key: string, fallback: SelectOption[] = []): SelectOption[] {
  const { data } = useQuery({
    queryKey: ['value-sets', key],
    queryFn: () => valueSetsService.getByKey(key, 'en'),
    staleTime: 5 * 60 * 1000,
  });
  return data?.items?.length
    ? data.items.map((i) => ({ value: i.code, label: i.label }))
    : fallback;
}
